import type {
  Announcement,
  Checklist,
  ResourceCenterConfig,
  Theme,
  Tour,
} from './types';

// ---------- Module state ----------
//
// The Resource Center runs in its own Shadow DOM root, parallel to the tour
// and checklist runtimes. Single-instance per page (one launcher, one panel).
// Mounts on first config delivery; subsequent identifies/route changes
// re-render content into the existing root.

type RcState = {
  host: HTMLElement | null;
  shadow: ShadowRoot | null;
  config: ResourceCenterConfig | null;
  theme: Theme | null;
  tours: Tour[];
  checklists: Checklist[];
  announcements: Announcement[];
  open: boolean;
};

const rc: RcState = {
  host: null,
  shadow: null,
  config: null,
  theme: null,
  tours: [],
  checklists: [],
  announcements: [],
  open: false,
};

export type ResourceCenterCallbacks = {
  /** Launch a tour by id (drops the panel and starts the tour). */
  launchTour: (tourId: string) => void;
  /** Re-open a checklist widget by id (un-dismiss + force expand). */
  openChecklist: (checklistId: string) => void;
  /** Re-show a previously-dismissed announcement by id. */
  openAnnouncement: (announcementId: string) => void;
  /** Telemetry: panel was opened. */
  reportOpen: () => void;
  /** Telemetry: a custom link was clicked. */
  reportLinkClick: (linkId: string) => void;
  /** Telemetry: a tour was launched from the resource center. */
  reportTourLaunched: (tourId: string) => void;
  /** Telemetry: a checklist was opened from the resource center. */
  reportChecklistOpened: (checklistId: string) => void;
  /** Telemetry: an announcement was opened from the resource center. */
  reportAnnouncementOpened: (announcementId: string) => void;
};

let callbacks: ResourceCenterCallbacks | null = null;

const SHADOW_CSS = `
:host {
  font-size: 14px;
  line-height: 1.5;
  color: #0f172a;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
* { box-sizing: border-box; font-family: var(--steppr-font-family, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif); }
button { font: inherit; cursor: pointer; border: none; background: none; }

.launcher {
  position: fixed;
  bottom: 20px;
  z-index: 2147483601;
  width: 48px;
  height: 48px;
  border-radius: 999px;
  background: var(--steppr-primary, #2563eb);
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 25px -10px rgba(0, 0, 0, 0.4);
  transition: filter 120ms ease, transform 120ms ease;
}
.launcher:hover { filter: brightness(1.08); transform: translateY(-1px); }
.launcher.right { right: 20px; }
.launcher.left  { left: 20px; }
.launcher svg { width: 22px; height: 22px; }

.panel {
  position: fixed;
  bottom: 80px;
  z-index: 2147483600;
  width: 340px;
  max-height: min(70vh, 560px);
  background: #ffffff;
  border-radius: var(--steppr-radius, 10px);
  box-shadow: 0 20px 50px -10px rgba(0, 0, 0, 0.35), 0 4px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transform-origin: bottom center;
  animation: rcIn 140ms ease-out;
}
.panel.right { right: 20px; transform-origin: bottom right; }
.panel.left  { left: 20px;  transform-origin: bottom left; }
@keyframes rcIn {
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

.header {
  padding: 16px 16px 12px;
  border-bottom: 1px solid #e5e7eb;
}
.title { font-size: 15px; font-weight: 600; margin: 0 0 2px; color: #0f172a; }
.intro { font-size: 13px; color: #475569; margin: 4px 0 0; }
.close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
}
.close:hover { background: #f1f5f9; color: #0f172a; }

.body { overflow-y: auto; padding: 8px 0; }

.section { padding: 8px 16px; }
.section-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
  margin: 6px 0 6px;
}

.row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  text-align: left;
  color: #0f172a;
  text-decoration: none;
}
.row:hover, button.row:hover { background: #f8fafc; }
.row .icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--steppr-primary, #2563eb) 12%, #ffffff);
  color: var(--steppr-primary, #2563eb);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.row .icon svg { width: 14px; height: 14px; }
.row .label { font-size: 13px; font-weight: 500; }
.row .meta { font-size: 11px; color: #64748b; margin-top: 1px; }

.empty {
  color: #64748b;
  font-size: 13px;
  text-align: center;
  padding: 24px 16px;
}

.brand {
  text-align: center;
  font-size: 11px;
  color: #94a3b8;
  padding: 8px 16px 12px;
  border-top: 1px solid #e5e7eb;
}
.brand a { color: inherit; text-decoration: underline; }
`;

function ensureMount(): void {
  if (rc.host && rc.shadow) return;
  const host = document.createElement('div');
  host.setAttribute('data-steppr-resource-center', '');
  document.documentElement.appendChild(host);
  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = SHADOW_CSS;
  shadow.appendChild(style);
  rc.host = host;
  rc.shadow = shadow;
}

function unmount(): void {
  if (rc.host && rc.host.parentNode) rc.host.parentNode.removeChild(rc.host);
  rc.host = null;
  rc.shadow = null;
  rc.open = false;
}

function applyThemeVars(): void {
  if (!rc.host || !rc.theme) return;
  rc.host.style.setProperty('--steppr-primary', rc.theme.primaryColor);
  // Match the radius mapping used by the tour runtime.
  const radiusPx = rc.theme.radius === 'sm' ? '6px' : rc.theme.radius === 'lg' ? '14px' : '10px';
  rc.host.style.setProperty('--steppr-radius', radiusPx);
}

// ---------- Rendering ----------

const HELP_ICON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';
const TOUR_ICON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="3 7 9 4 15 7 21 4 21 17 15 20 9 17 3 20"/></svg>';
const CHECK_ICON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>';
const ANN_ICON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11l18-7v17l-18-7V11z"/></svg>';
const LINK_ICON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1"/><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"/></svg>';
const X_ICON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

function renderLauncher(): void {
  if (!rc.shadow || !rc.config) return;
  // Strip any previous launcher, then redraw.
  rc.shadow.querySelectorAll('.launcher').forEach((n) => n.remove());
  const btn = document.createElement('button');
  btn.className = 'launcher ' + (rc.config.position === 'bottom-left' ? 'left' : 'right');
  btn.setAttribute('aria-label', 'Open ' + (rc.config.title ?? 'Resources'));
  btn.innerHTML = HELP_ICON_SVG;
  btn.addEventListener('click', () => togglePanel());
  rc.shadow.appendChild(btn);
}

function togglePanel(): void {
  rc.open = !rc.open;
  if (rc.open) {
    callbacks?.reportOpen();
    renderPanel();
  } else {
    rc.shadow?.querySelectorAll('.panel').forEach((n) => n.remove());
  }
}

// Open the panel programmatically. Only the marketing demo (demo.ts) calls
// this; esbuild drops it from steppr.js as unused.
export function openResourceCenter(): void {
  if (rc.config && !rc.open) togglePanel();
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function isHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

function renderPanel(): void {
  if (!rc.shadow || !rc.config) return;
  rc.shadow.querySelectorAll('.panel').forEach((n) => n.remove());

  const panel = document.createElement('div');
  panel.className = 'panel ' + (rc.config.position === 'bottom-left' ? 'left' : 'right');

  // ----- Header -----
  const header = document.createElement('div');
  header.className = 'header';
  const title = document.createElement('p');
  title.className = 'title';
  title.textContent = rc.config.title?.trim() || 'Resources';
  header.appendChild(title);
  if (rc.config.helpText && rc.config.helpText.trim().length) {
    const intro = document.createElement('p');
    intro.className = 'intro';
    intro.textContent = rc.config.helpText.trim();
    header.appendChild(intro);
  }
  const close = document.createElement('button');
  close.className = 'close';
  close.setAttribute('aria-label', 'Close');
  close.innerHTML = X_ICON_SVG;
  close.addEventListener('click', () => togglePanel());
  header.appendChild(close);
  // Position absolute requires a positioning context.
  header.style.position = 'relative';
  panel.appendChild(header);

  const body = document.createElement('div');
  body.className = 'body';
  panel.appendChild(body);

  let renderedAnything = false;

  // ----- Tours -----
  if (rc.config.sections.tours.visible) {
    const visibleTours = rc.tours.filter((t) => {
      const cfg = t.snapshot.config as { hideFromResourceCenter?: boolean } | undefined;
      return !cfg?.hideFromResourceCenter;
    });
    if (visibleTours.length > 0) {
      renderedAnything = true;
      const section = document.createElement('div');
      section.className = 'section';
      section.innerHTML = '<p class="section-title">Tours</p>';
      for (const t of visibleTours) {
        const row = document.createElement('button');
        row.className = 'row';
        row.type = 'button';
        row.innerHTML = `<span class="icon">${TOUR_ICON_SVG}</span><span class="label">${escapeHtml(t.name)}</span>`;
        row.addEventListener('click', () => {
          callbacks?.reportTourLaunched(t.id);
          togglePanel();
          callbacks?.launchTour(t.id);
        });
        section.appendChild(row);
      }
      body.appendChild(section);
    }
  }

  // ----- Checklists -----
  if (rc.config.sections.checklists.visible) {
    const visibleChecklists = rc.checklists.filter((c) => {
      const cfg = c.snapshot.config as { hideFromResourceCenter?: boolean } | undefined;
      return !cfg?.hideFromResourceCenter;
    });
    if (visibleChecklists.length > 0) {
      renderedAnything = true;
      const section = document.createElement('div');
      section.className = 'section';
      section.innerHTML = '<p class="section-title">Checklists</p>';
      for (const c of visibleChecklists) {
        const total = c.snapshot.items.length;
        const done = Object.keys(c.state.items ?? {}).length;
        const row = document.createElement('button');
        row.className = 'row';
        row.type = 'button';
        row.innerHTML = `
          <span class="icon">${CHECK_ICON_SVG}</span>
          <span>
            <div class="label">${escapeHtml(c.name)}</div>
            <div class="meta">${done} of ${total} done</div>
          </span>`;
        row.addEventListener('click', () => {
          callbacks?.reportChecklistOpened(c.id);
          togglePanel();
          callbacks?.openChecklist(c.id);
        });
        section.appendChild(row);
      }
      body.appendChild(section);
    }
  }

  // ----- Announcements (recent history, including dismissed) -----
  if (rc.config.sections.announcements.visible) {
    const visibleAnnouncements = rc.announcements.filter((a) => {
      const cfg = a.snapshot.config as { hideFromResourceCenter?: boolean } | undefined;
      return !cfg?.hideFromResourceCenter;
    });
    if (visibleAnnouncements.length > 0) {
      renderedAnything = true;
      const limit = Math.max(1, Math.min(20, rc.config.sections.announcements.limit));
      const recent = visibleAnnouncements.slice(0, limit);
      const section = document.createElement('div');
      section.className = 'section';
      section.innerHTML = '<p class="section-title">Recent announcements</p>';
      for (const a of recent) {
        const row = document.createElement('button');
        row.className = 'row';
        row.type = 'button';
        const heading = a.snapshot.title?.trim() || a.name;
        const dismissedTag = a.state?.dismissed
          ? '<div class="meta">Dismissed</div>'
          : '';
        row.innerHTML = `
          <span class="icon">${ANN_ICON_SVG}</span>
          <span>
            <div class="label">${escapeHtml(heading)}</div>
            ${dismissedTag}
          </span>`;
        row.addEventListener('click', () => {
          callbacks?.reportAnnouncementOpened(a.id);
          togglePanel();
          callbacks?.openAnnouncement(a.id);
        });
        section.appendChild(row);
      }
      body.appendChild(section);
    }
  }

  // ----- Custom links -----
  if (rc.config.sections.links.length > 0) {
    renderedAnything = true;
    const section = document.createElement('div');
    section.className = 'section';
    section.innerHTML = '<p class="section-title">Help & docs</p>';
    for (const link of rc.config.sections.links) {
      if (!link.label.trim() || !isHttpUrl(link.url)) continue;
      const a = document.createElement('a');
      a.className = 'row';
      a.href = link.url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.innerHTML = `<span class="icon">${LINK_ICON_SVG}</span><span class="label">${escapeHtml(link.label)}</span>`;
      a.addEventListener('click', () => {
        callbacks?.reportLinkClick(link.id);
      });
      section.appendChild(a);
    }
    body.appendChild(section);
  }

  if (!renderedAnything) {
    const empty = document.createElement('div');
    empty.className = 'empty';
    empty.textContent = 'Nothing to show yet.';
    body.appendChild(empty);
  }

  // ----- Branding -----
  if (!rc.theme?.hideBranding) {
    const brand = document.createElement('div');
    brand.className = 'brand';
    brand.innerHTML = 'Powered by <a href="https://steppr.io" target="_blank" rel="noopener noreferrer">Steppr</a>';
    panel.appendChild(brand);
  }

  rc.shadow.appendChild(panel);
}

// ---------- Public API ----------

/**
 * Boot or update the Resource Center. Called once on session boot, again on
 * identify, and any time the source data list changes. When `config` is null
 * we tear down the widget entirely (workspace disabled it, MAU cap hit, etc.).
 */
export function bootResourceCenter(args: {
  config: ResourceCenterConfig | null;
  callbacks: ResourceCenterCallbacks;
  theme: Theme;
  tours: Tour[];
  checklists: Checklist[];
  announcements: Announcement[];
}): void {
  callbacks = args.callbacks;
  rc.theme = args.theme;
  rc.tours = args.tours;
  rc.checklists = args.checklists;
  rc.announcements = args.announcements;

  if (!args.config || !args.config.enabled) {
    unmount();
    rc.config = null;
    return;
  }

  rc.config = args.config;
  ensureMount();
  applyThemeVars();
  renderLauncher();
  // Re-render the open panel if the user had it open and content changed.
  if (rc.open) renderPanel();
}

export function applyResourceCenterTheme(theme: Theme): void {
  rc.theme = theme;
  if (rc.host) applyThemeVars();
  if (rc.open) renderPanel();
}
